import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, type HTMLMotionProps } from "framer-motion";

import { cn } from "@/lib/utils";

// Props that conflict between DOM and Framer Motion
type ConflictingProps = 
  | "onDrag" 
  | "onDragStart" 
  | "onDragEnd" 
  | "onAnimationStart" 
  | "onAnimationEnd"
  | "onAnimationIteration";

// Clean button props: standard HTML button props minus conflicting ones
type CleanButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, ConflictingProps>;

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-normal rounded-md text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:shadow-md hover:bg-primary/92 active:bg-primary/85",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/92 active:bg-destructive/85",
        outline:
          "border border-input bg-background hover:bg-secondary/80 hover:text-secondary-foreground active:bg-secondary",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/70 active:bg-secondary/60",
        ghost: "hover:bg-accent/80 hover:text-accent-foreground active:bg-accent",
        link: "text-primary underline-offset-4 hover:underline active:opacity-80",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends CleanButtonProps,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  animated?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      animated = true,
      disabled,
      type,
      children,
      ...rest
    },
    ref,
  ) => {
    const classes = cn(buttonVariants({ variant, size, className }));

    if (asChild) {
      return (
        <Slot className={classes} ref={ref} {...rest}>
          {children}
        </Slot>
      );
    }

    const buttonType = type ?? "button";

    if (animated && !disabled) {
      return (
        <motion.button
          className={classes}
          ref={ref}
          type={buttonType}
          disabled={disabled}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", stiffness: 500, damping: 25 }}
          {...(rest as Omit<HTMLMotionProps<"button">, "ref">)}
        >
          {children}
        </motion.button>
      );
    }

    return (
      <button className={classes} ref={ref} type={buttonType} disabled={disabled} {...rest}>
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };


